import React, { useEffect, useState } from 'react';
import { Loader2, Send, CheckCircle2 } from 'lucide-react';
import { actionApi, doneStatus, markDone } from './drGActions';

const field = 'w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200';

export const ConfirmShell = ({ title, icon, status, busy, err, onConfirm, onCancel, children, testid }) => (
  <div className="rounded-xl border border-sky-200 bg-white p-3 text-sm space-y-2" data-testid={testid}>
    <p className="font-semibold text-slate-800 flex items-center gap-1.5">{icon} {title}</p>
    {status ? (
      <p className={`flex items-center gap-1.5 ${status === 'done' ? 'text-emerald-700' : 'text-slate-500'}`} data-testid={`${testid}-status`}>
        {status === 'done' ? <><CheckCircle2 className="w-4 h-4" /> Done</> : 'Cancelled'}
      </p>
    ) : (
      <>
        {children}
        {err && <p className="text-rose-600 text-xs" data-testid={`${testid}-error`}>{err}</p>}
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onConfirm} disabled={busy} data-testid={`${testid}-confirm`}
            className="rounded-full bg-sky-600 hover:bg-sky-700 text-white px-4 py-1.5 text-xs font-semibold disabled:opacity-60 inline-flex items-center gap-1">
            {busy && <Loader2 className="w-3 h-3 animate-spin" />} Confirm
          </button>
          <button type="button" onClick={onCancel} data-testid={`${testid}-cancel`} className="rounded-full border px-4 py-1.5 text-xs text-slate-600 hover:bg-slate-50">Cancel</button>
        </div>
      </>
    )}
  </div>
);

export const useConfirm = (doneKey, run) => {
  const [status, setStatus] = useState(() => doneStatus(doneKey));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const finish = (s) => { markDone(doneKey, s); setStatus(s); };
  const confirm = async () => {
    setBusy(true); setErr('');
    try { await run(); finish('done'); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return { status, busy, err, confirm, cancel: () => finish('cancelled') };
};

export const SendMessageCard = ({ action, doneKey }) => {
  const [patients, setPatients] = useState(null);
  const [pid, setPid] = useState('');
  const [f, setF] = useState({ channel: action.channel || 'sms', sms: action.sms || '', emailSubject: action.emailSubject || '', emailBody: action.emailBody || '' });
  useEffect(() => { actionApi.findPatients(action.patient).then((r) => { setPatients(r); if (r.length === 1) setPid(r[0].id); }).catch(() => setPatients([])); }, [action.patient]);
  const c = useConfirm(doneKey, () => {
    if (!pid) throw new Error('Choose the patient first.');
    return actionApi.sendMessage({ patientId: pid, ...f });
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <ConfirmShell title={`Send a message to ${action.patient}`} icon={<Send className="w-4 h-4 text-sky-600" />} testid="dr-g-send-card" {...c} onConfirm={c.confirm} onCancel={c.cancel}>
      {!patients ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : !patients.length ? <p className="text-rose-600 text-xs">No patient found named "{action.patient}".</p> : (
        <div className="space-y-1">
          {patients.map((p) => (
            <label key={p.id} className="flex items-center gap-2 text-xs">
              <input type="radio" name={`pt-${doneKey}`} checked={pid === p.id} onChange={() => setPid(p.id)} data-testid={`dr-g-send-patient-${p.id}`} />
              {p.firstName} {p.lastName} <span className="text-slate-400">{p.phone || p.email}</span>
            </label>
          ))}
        </div>
      )}
      <select value={f.channel} onChange={set('channel')} className={field} data-testid="dr-g-send-channel">
        <option value="sms">Text</option><option value="email">Email</option><option value="both">Text + Email</option>
      </select>
      {f.channel !== 'email' && <textarea value={f.sms} onChange={set('sms')} rows={3} className={field} placeholder="Text message" data-testid="dr-g-send-sms" />}
      {f.channel !== 'sms' && <>
        <input value={f.emailSubject} onChange={set('emailSubject')} className={field} placeholder="Email subject" data-testid="dr-g-send-subject" />
        <textarea value={f.emailBody} onChange={set('emailBody')} rows={4} className={field} placeholder="Email message" data-testid="dr-g-send-body" />
      </>}
    </ConfirmShell>
  );
};
